<!-- src/views/mailings/Mailings.vue -->
<template>
  <div class="mailings-container">
    <vs-row>
      <vs-col vs-w="12">
        <vx-card>
          <!-- Button to open creation popup -->
          <div class="mb-4">
            <vs-button
              color="primary"
              @click="createDialog.active = true"
            >
              Create new mailing
            </vs-button>
          </div>

          <!-- List -->
          <div class="mt-4">
            <vs-table :data="mailings" >
              <template slot-scope="{data}">
                <vs-tr v-for="tr in data" :key="tr.id">
                  <vs-td>{{ tr.name }}</vs-td>
                  <vs-td>{{ new Date(tr.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) }}</vs-td>
                  <vs-td>
                    <vs-button
                      color="primary"
                      type="border"
                      @click="openSendDialog(tr)"
                    >
                      Send
                    </vs-button>
                    <vs-button
                      color="primary"
                      class="ml-2"
                      @click="viewResults(tr)"
                    >
                      Results
                    </vs-button>
                    <vs-button
                      color="danger"
                      class="ml-2"
                      @click="viewUnsubscribed(tr)"
                    >
                      Unsubscribed → {{ getUnsubscribedCount(tr.id) }}
                    </vs-button>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </vx-card>
      </vs-col>
    </vs-row>

    <!-- Creation Popup -->
    <vs-popup
      :active.sync="createDialog.active"
      title="Create new mailing"
    >
      <div class="p-4">
        <vs-input
          v-model="newMailing.name"
          placeholder="Mailing name"
          class="w-full mb-4"
        />
        <vs-button
          color="primary"
          @click="createMailing"
        >
          Create
        </vs-button>
      </div>
    </vs-popup>

    <!-- Send Popup -->
    <vs-popup
      fullscreen
      :title="`Send mailing${sendDialog.mailing ? ' - ' + sendDialog.mailing.name : ''}`"
      :active.sync="sendDialog.active"
    >
      <div class="p-4">
        <!-- People list -->
        <div class="mt-4">
          <div class="flex justify-between items-center mb-4">
            <h4>Recipients ({{ selectedCount }} selected)</h4>
            <vs-button
              color="primary"
              type="border"
              @click="toggleSelectAll"
            >
              {{ isAllSelected ? 'Deselect All' : 'Select All' }}
            </vs-button>
          </div>
          <vs-table
            :data="filteredPeople"
            :max-items="50"
            pagination
            search
          >
            <template slot="header">
              <vs-th class="w-1/4">Name</vs-th>
              <vs-th class="w-1/4">Email</vs-th>
              <vs-th class="w-1/4">Language</vs-th>
              <vs-th class="w-1/4">Category</vs-th>
              <vs-th class="w-24">Actions</vs-th>
            </template>

            <template slot-scope="{data}">
              <vs-tr v-for="person in data" :key="person.id">
                <vs-td class="w-1/4">{{ person.name }}</vs-td>
                <vs-td class="w-1/4">{{ person.email }}</vs-td>
                <vs-td class="w-1/4">{{ person.language }}</vs-td>
                <vs-td class="w-1/4">{{ person.category }}</vs-td>
                <vs-td class="w-24">
                  <vs-checkbox
                    :value="selectedPeople[person.id]"
                    @change="() => togglePersonSelection(person.id)"
                    :disabled="isPersonPending(person.id)"
                  />
                  <span v-if="isPersonPending(person.id)" class="text-xs text-gray-500 ml-2">
                    (Already sent)
                  </span>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </div>

        <div class="mt-4 flex justify-end">
          <vs-button
            color="danger"
            border
            @click="sendDialog.active = false"
            :disabled="sendDialog.loading"
          >
            Cancel
          </vs-button>
          <vs-button
            class="ml-2"
            @click="sendMailing"
            :loading="sendDialog.loading"
            :disabled="sendDialog.loading"
          >
            Send
          </vs-button>
        </div>
      </div>
    </vs-popup>

    <!-- Unsubscribed Popup -->
    <vs-popup
      fullscreen
      :active.sync="unsubscribedDialog.active"
      :title="`Unsubscribed Users${unsubscribedDialog.mailing ? ' - ' + unsubscribedDialog.mailing.name : ''}`"
    >
      <div class="p-4">
        <vs-table :data="unsubscribedUsers">
          <template slot-scope="{data}">
            <vs-tr v-for="user in data" :key="user.company_name">
              <vs-td>{{ user.company_name }}</vs-td>
              <vs-td>{{ user.unsubscribed_after }} mailings</vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>
    </vs-popup>

    <!-- Results Popup -->
    <vs-popup
      fullscreen
      :active.sync="resultsDialog.active"
      :title="`Mailing Results`"
    > 
    <vs-button
      icon
      color="primary"
      @click="downloadNPS(resultsDialog.mailing.id)"
    >
      <i class="bx bx-download mr-2"></i>
      Export XLS
    </vs-button>
      <div class="p-4">
        <!-- Global percentages -->
        <div class="mb-6">
          <h4 class="mb-4">Global Statistics</h4>
          <div class="vx-row">
            <div class="vx-col w-full md:w-1/3 mb-4">
              <vx-card>
                <h5>Link Clicks</h5>
                <div class="flex items-center">
                  <vs-progress :percent="clickPercentage" color="success" class="flex-1 mr-2" />
                  <span>{{ clickPercentage }}%</span>
                </div>
                <p class="mt-2 text-sm text-gray-600">{{ clickedCount }} out of {{ results.length }} people clicked the link</p>
              </vx-card>
            </div>
            <div class="vx-col w-full md:w-1/3 mb-4" v-for="(pct, index) in questionPercentages" :key="index">
              <vx-card>
                <h5>Q{{ index + 1 }}: {{ questions[index] }}</h5>
                <div class="flex items-center">
                  <vs-progress :percent="pct" color="primary" class="flex-1 mr-2" />
                  <span>{{ pct }}%</span>
                </div>
              </vx-card>
            </div>
          </div>
        </div>

        <!-- Response details -->
        <h4 class="mb-4">Response Details</h4>
        <vs-table
          v-if="results.length"
          :data="results"
            :max-items="50"
            pagination
            search
        >
          <template slot-scope="{data}">
            <vs-tr v-for="tr in data" :key="tr.id">
              <vs-td class="w-1/8">{{ tr.person.company_name }}</vs-td>
              <vs-td class="w-1/8">{{ tr.person.email }}</vs-td>
              <vs-td class="w-1/8">
                <vs-chip v-if="tr.clicked_at" color="success">
                  {{ new Date(tr.clicked_at).toLocaleString() }}
                </vs-chip>
                <vs-chip v-else color="danger">Not clicked</vs-chip>
              </vs-td>
              <vs-td class="w-1/12">
                <vs-chip :color="getResponseColor(tr.q1)">Q1: {{ tr.q1 }}</vs-chip>
              </vs-td>
              <vs-td class="w-1/12">
                <vs-chip :color="getResponseColor(tr.q2)">Q2: {{ tr.q2 }}</vs-chip>
              </vs-td>
              <vs-td class="w-1/12">
                <vs-chip :color="getResponseColor(tr.q3)">Q3: {{ tr.q3 }}</vs-chip>
              </vs-td>
              <vs-td class="w-1/12">
                <vs-chip :color="getResponseColor(tr.q4)">Q4: {{ tr.q4 }}</vs-chip>
              </vs-td>
              <vs-td class="w-1/12">
                <vs-chip :color="getResponseColor(tr.q5)">Q5: {{ tr.q5 }}</vs-chip>
              </vs-td>
              <vs-td class="w-1/12">
                <vs-chip :color="getResponseColor(tr.q6)">Q6: {{ tr.q6 }}</vs-chip>
              </vs-td>
              <vs-td class="w-1/8">
                <template v-if="tr.comments">
                  <vs-button
                    color="primary"
                    type="flat"
                    @click="showComments(tr)"
                  >
                    <i class="mdi mdi-comment-text-outline mr-1"></i>
                    View comments
                  </vs-button>
                </template>
                <vs-chip v-else color="warning">No comments</vs-chip>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
        <div v-else>
          No results yet.
        </div>
      </div>
    </vs-popup>

    <!-- Comments Modal -->
    <div v-if="commentsDialog.active" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 999999; display: flex; align-items: center; justify-content: center;">
      <div style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5);" @click="commentsDialog.active = false"></div>
      <div style="position: relative; background: white; padding: 20px; border-radius: 8px; width: 90%; max-width: 600px; max-height: 80vh; overflow-y: auto;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
          <h3>Comments from {{ commentsDialog.personName }}</h3>
          <vs-button
            color="danger"
            type="flat"
            @click="commentsDialog.active = false"
          >
            <i class="mdi mdi-close"></i>
          </vs-button>
        </div>
        <p class="whitespace-pre-wrap">{{ commentsDialog.content }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios.js";

export default {
  name: 'Mailings',

  data() {
    return {
      mailings: [],
      newMailing: {
        name: ''
      },
      createDialog: {
        active: false
      },
      sendDialog: {
        active: false,
        mailing: null,
        loading: false
      },
      resultsDialog: {
        active: false,
        mailing: null
      },
      unsubscribedDialog: {
        active: false,
        mailing: null
      },
      commentsDialog: {
        active: false,
        content: '',
        personName: ''
      },
      results: [],
      people: [],
      selectedPeople: {},
      languages: [],
      questionPercentages: [0, 0, 0, 0, 0, 0],
      questions: [
        "How likely are you to recommend Acacia Bois to a colleague or partner?",
        "Quality of received products",
        "Delivery times",
        "Quality of palletization and packaging",
        "Communication and responsiveness of our team",
        "Value for money of our products"
      ],
      pendingRecipients: [],
      selectedComment: null,
      unsubscribedUsers: []
    }
  },

  computed: {
    filteredPeople() {
      return this.people.filter(person =>
        person.email &&
        person.email.trim() !== '' &&
        !person.unsubscribed &&
        person.category &&
        person.category.includes('Client')
      )
    },
    isAllSelected() {
      return this.filteredPeople.length > 0 &&
             this.filteredPeople.every(person => this.selectedPeople[person.id])
    },
    selectedCount() {
      return Object.values(this.selectedPeople).filter(Boolean).length
    },
    clickedCount() {
      return this.results.filter(r => r.clicked_at).length
    },
    clickPercentage() {
      if (!this.results.length) return 0
      return Math.round((this.clickedCount / this.results.length) * 100)
    }
  },

  async created() {
    await this.loadMailings()
    await this.loadPeople()
    await this.loadUnsubscribedUsers()
  },

  methods: {
      downloadNPS(idnps) {
    window.open("https://xylo.codesinn.com/api/kpi/nps/"+idnps+"/d86f4d7f-65d3-413e-aed4-2ae3816d5145", "_blank");
  },
    async loadMailings() {
      try {
        const response = await axios.get('/mailings')
        this.mailings = response.data
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to load mailings'
        })
      }
    },

    async createMailing() {
      if (!this.newMailing.name) return

      try {
        await axios.post('/mailings', this.newMailing)
        this.newMailing.name = ''
        this.createDialog.active = false
        await this.loadMailings()
        this.$vs.notify({
          color: 'success',
          title: 'Success',
          text: 'Mailing created successfully'
        })
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to create mailing'
        })
      }
    },

    async loadPeople() {
      try {
        const response = await axios.get('/people/data')
        this.people = response.data
        // Extract unique languages
        this.languages = [...new Set(this.people.map(p => p.language))]
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to load people'
        })
      }
    },

    async loadPendingRecipients() {
      try {
        const response = await axios.get('/mailings/pending')
        this.pendingRecipients = response.data
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to load pending recipients'
        })
      }
    },

    openSendDialog(mailing) {
      this.sendDialog.mailing = mailing
      this.sendDialog.active = true
      this.selectedPeople = {}
      this.loadPendingRecipients()
    },

    togglePersonSelection(personId) {
      this.$set(this.selectedPeople, personId, !this.selectedPeople[personId])
    },

    toggleSelectAll() {
      const newState = !this.isAllSelected
      this.filteredPeople.forEach(person => {
        if (!this.isPersonPending(person.id)) {
          this.$set(this.selectedPeople, person.id, newState)
        }
      })
    },

    async sendMailing() {
      if (!this.sendDialog.mailing || !this.selectedCount) return
      this.sendDialog.loading = true
      try {
        await axios.post(`/mailings/${this.sendDialog.mailing.id}/send`, {
          peopleIds: Object.entries(this.selectedPeople)
            .filter(([_, selected]) => selected)
            .map(([id]) => id)
        })
        this.sendDialog.active = false
        await this.loadPendingRecipients()
        this.selectedPeople = {}
        this.$vs.notify({
          color: 'success',
          title: 'Envoi en cours',
          text: 'Le serveur prend le relai! Vous pouvez suivre l\'avancement dans Results.'
        })
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to send mailing'
        })
      } finally {
        this.sendDialog.loading = false
      }
    },

    async viewResults(mailing) {
      try {
        const response = await axios.get(`/mailings/${mailing.id}/results`)
        this.results = response.data
        this.resultsDialog.mailing = mailing
        this.resultsDialog.active = true
        this.calculatePercentages()
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to load results'
        })
      }
    },

    calculatePercentages() {
      if (!this.results.length) return

      const questionCounts = Array(6).fill(0)
      const questionResponses = Array(6).fill(0)

      this.results.forEach(result => {
        // Q1 is on a scale of 1-10
        if (result.q1 && parseInt(result.q1) > 0) {
          questionCounts[0] += parseInt(result.q1)
          questionResponses[0]++
        }
        // Q2-Q6 are on a scale of 1-5
        for (let i = 2; i <= 6; i++) {
          if (result[`q${i}`] && parseInt(result[`q${i}`]) > 0) {
            questionCounts[i - 1] += parseInt(result[`q${i}`])
            questionResponses[i - 1]++
          }
        }
      })

      // Calculate averages
      this.questionPercentages = questionCounts.map((count, index) => {
        if (questionResponses[index] === 0) return 0
        // For Q1: convert to percentage based on scale of 1-10
        if (index === 0) {
          return Math.round((count / questionResponses[index] / 10) * 100)
        }
        // For Q2-Q6: convert to percentage based on scale of 1-5
        return Math.round((count / questionResponses[index] / 5) * 100)
      })
    },

    getResponseColor(response) {
      if (!response) return 'danger'
      if (response === 'Yes') return 'success'
      if (response === 'No') return 'danger'
      return 'primary'
    },

    isPersonPending(personId) {
      return this.pendingRecipients.some(p => p.idperson === personId)
    },

    showComments(result) {
      this.commentsDialog.content = result.comments;
      this.commentsDialog.personName = result.person.company_name;
      this.commentsDialog.active = true;
    },

    async loadUnsubscribedUsers() {
      try {
        const response = await axios.get('/mailings/unsubscribed')
        this.unsubscribedUsers = response.data
      } catch (error) {
        this.$vs.notify({
          color: 'danger',
          title: 'Error',
          text: 'Unable to load unsubscribed users'
        })
      }
    },

    getUnsubscribedCount(mailingId) {
      return this.unsubscribedUsers.length
    },

    viewUnsubscribed(mailing) {
      this.unsubscribedDialog.mailing = mailing
      this.unsubscribedDialog.active = true
    }
  }
}
</script>

<style scoped>
.comments-popup {
  z-index: 1 !important;
}
</style>
